import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { PageCard } from '../../components/layout/page/PageCard';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { SettingsTabBar } from '../../components/settings/SettingsTabBar';
import { SettingsTabItem } from '../../components/settings/SettingsTabItem';
import { DraggableModalPanel } from '../../components/ui/DraggableModalPanel';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { PasswordInput } from '../../components/ui/PasswordInput';
import { usePermissions } from '../../contexts/PermissionsContext';
import {
  DecisionEngineLocalStatus,
  DecisionEngineSettings,
  DecisionEngineTestAnswer,
  DecisionEngineTestResult,
  fetchDecisionEngineLocal,
  fetchDecisionEngineSettings,
  resetDecisionEngineLocal,
  saveDecisionEngineSettings,
  startDecisionEngineLocal,
  stopDecisionEngineLocal,
  testDecisionEngine,
} from '../../services/decisionEngineApi';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';

type DecisionEngineTabId = 'settings' | 'usage' | 'connection';
type QuestionType = 'choice' | 'score' | 'noul';

const TABS: SettingsTabItem<DecisionEngineTabId>[] = [
  { id: 'settings', label: 'Configurações' },
  { id: 'usage', label: 'Uso' },
  { id: 'connection', label: 'Conexão' },
];

const CHECKPOINTS = [
  { value: 'multilingual', label: 'Multilíngue' },
  { value: 'english', label: 'Inglês' },
  { value: 'typed-decisions', label: 'Decisões tipadas' },
];

const LANGUAGES = [
  { value: 'pt', label: 'Português' },
  { value: 'en', label: 'Inglês' },
  { value: 'es', label: 'Espanhol' },
  { value: 'fr', label: 'Francês' },
  { value: 'de', label: 'Alemão' },
  { value: 'it', label: 'Italiano' },
];

const DEFAULT_STATE = 'Fui cobrado duas vezes na fatura e preciso do estorno ainda hoje.';
const CHOICE_INSTRUCTIONS = 'Qual fila deve tratar este texto?';
const SCORE_INSTRUCTIONS = 'Qual a urgência deste texto?';
const NOUL_INSTRUCTIONS = 'Este texto pede estorno?';
const CHOICE_OPTIONS = [
  'financeiro: faturas, pagamentos e estornos',
  'tecnico: falhas e indisponibilidade',
  'outro: qualquer outro assunto',
].join('\n');
const SCORE_OPTIONS = ['baixa', 'media', 'alta'].join('\n');

function instructionsFor(type: QuestionType): string {
  if (type === 'score') {
    return SCORE_INSTRUCTIONS;
  }

  if (type === 'noul') {
    return NOUL_INSTRUCTIONS;
  }

  return CHOICE_INSTRUCTIONS;
}

function optionsFor(type: QuestionType): string {
  if (type === 'score') {
    return SCORE_OPTIONS;
  }

  if (type === 'noul') {
    return '';
  }

  return CHOICE_OPTIONS;
}

function parseOptionLines(raw: string, type: QuestionType): {
  criteria?: Record<string, string>;
  levels?: string[];
} {
  const lines = raw
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  if (type === 'score') {
    return { levels: lines };
  }

  if (type === 'noul') {
    return {};
  }

  const criteria: Record<string, string> = {};
  lines.forEach((line, index) => {
    const separator = line.indexOf(':');
    if (separator <= 0) {
      criteria[`opcao${index + 1}`] = line;
      return;
    }

    const key = line.slice(0, separator).trim();
    const description = line.slice(separator + 1).trim();
    criteria[key || `opcao${index + 1}`] = description;
  });
  return { criteria };
}

function DecisionEngineHelp() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        className="inline-flex shrink-0 rounded-full p-0.5 text-secondary-400 transition-colors hover:bg-primary-50 hover:text-primary-600"
        aria-label="Ajuda sobre o motor de decisão"
        onClick={() => setOpen(true)}
      >
        <QuestionMarkCircleIcon className="h-5 w-5" aria-hidden="true" />
      </button>
      <DraggableModalPanel
        open={open}
        title="Motor de decisão"
        onClose={() => setOpen(false)}
      >
        <p>Classifica um texto em escolha, nota ou sim/não. Não escreve mensagem.</p>
        <p className="mt-2">
          Módulos desta empresa e sistemas externos usam essa resposta para seguir adiante.
        </p>
      </DraggableModalPanel>
    </>
  );
}

function scoreLevelName(
  key: string,
  levels: string[],
  legend?: Record<string, string> | null,
): string {
  const fromLegend = legend?.[key]?.trim();
  if (fromLegend) {
    return fromLegend;
  }

  const index = Number(key);
  if (Number.isInteger(index) && index >= 0 && index < levels.length) {
    return levels[index];
  }

  return key;
}

function formatScoreAnswer(answer: DecisionEngineTestAnswer, levels: string[]): string {
  let bestKey: string | null = null;
  let bestValue = -1;
  for (const [key, value] of Object.entries(answer.probabilities)) {
    if (value > bestValue) {
      bestValue = value;
      bestKey = key;
    }
  }

  if (bestKey != null) {
    return scoreLevelName(bestKey, levels, answer.legend);
  }

  if (answer.score == null) {
    return 'Sem nota';
  }

  const index = Math.round(answer.score);
  if (index >= 0 && index < levels.length) {
    return levels[index];
  }

  return 'Sem nota';
}

function formatAnswer(answer: DecisionEngineTestAnswer, levels: string[] = []): string {
  if (answer.type === 'score') {
    return formatScoreAnswer(answer, levels);
  }

  if (answer.type === 'noul') {
    return answer.noul == null ? 'Sem probabilidade' : `Sim ${(answer.noul * 100).toFixed(1)}%`;
  }

  return answer.choice || 'Sem escolha';
}

export function DecisionEnginePage() {
  const { hasPermission } = usePermissions();
  const canWrite = hasPermission('settings.decision-engine.write');
  const [activeTab, setActiveTab] = useState<DecisionEngineTabId>('settings');
  const [settings, setSettings] = useState<DecisionEngineSettings | null>(null);
  const [persisted, setPersisted] = useState<DecisionEngineSettings | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [clearApiKey, setClearApiKey] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [probeState, setProbeState] = useState(DEFAULT_STATE);
  const [probeType, setProbeType] = useState<QuestionType>('choice');
  const [probeInstructions, setProbeInstructions] = useState(CHOICE_INSTRUCTIONS);
  const [probeOptions, setProbeOptions] = useState(CHOICE_OPTIONS);
  const [testResult, setTestResult] = useState<DecisionEngineTestResult | null>(null);
  const [testedLevels, setTestedLevels] = useState<string[]>([]);
  const [localStatus, setLocalStatus] = useState<DecisionEngineLocalStatus | null>(null);
  const [localBusy, setLocalBusy] = useState(false);
  const [consoleOpen, setConsoleOpen] = useState(false);
  const consoleRef = useRef<HTMLPreElement>(null);
  const closeConsoleWhenReady = useRef(false);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      setLoadError('');
      try {
        const loaded = await fetchDecisionEngineSettings();
        setSettings(loaded);
        setPersisted(loaded);
      } catch (error) {
        const message = getRequestErrorMessage(error, 'Falha ao carregar o motor de decisão.');
        setLoadError(message);
        toast.error(message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (activeTab !== 'connection') {
      return undefined;
    }

    let cancelled = false;
    const load = async () => {
      try {
        const status = await fetchDecisionEngineLocal();
        if (!cancelled) {
          setLocalStatus(status);
        }
      } catch {
        if (!cancelled) {
          setLocalStatus(null);
        }
      }
    };

    void load();
    const timer = window.setInterval(() => {
      void load();
    }, consoleOpen ? 1500 : 4000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [activeTab, consoleOpen]);

  useEffect(() => {
    if (closeConsoleWhenReady.current && localStatus?.running) {
      closeConsoleWhenReady.current = false;
      setConsoleOpen(false);
    }
  }, [localStatus?.running]);

  useEffect(() => {
    if (!consoleOpen) {
      return;
    }

    const node = consoleRef.current;
    if (node) {
      node.scrollTop = node.scrollHeight;
    }
  }, [consoleOpen, localStatus?.lines]);

  async function handleSave(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!settings || !canWrite || saving) {
      return;
    }

    setSaving(true);
    try {
      const saved = await saveDecisionEngineSettings({
        baseUrl: settings.baseUrl,
        apiKey: apiKey.trim() || undefined,
        clearApiKey,
        defaultLanguage: settings.defaultLanguage,
        checkpoint: settings.checkpoint,
        timeoutSeconds: settings.timeoutSeconds,
        maxConcurrent: settings.maxConcurrent,
        maxConcurrentPerCaller: settings.maxConcurrentPerCaller,
        circuitFailureThreshold: settings.circuitFailureThreshold,
        circuitBreakSeconds: settings.circuitBreakSeconds,
        enabled: settings.enabled,
        customerSuccessEnabled: settings.customerSuccessEnabled,
        emailTriageEnabled: settings.emailTriageEnabled,
        chatMessagesEnabled: settings.chatMessagesEnabled,
        technologyEnabled: settings.technologyEnabled,
        monitoringEnabled: settings.monitoringEnabled,
      });
      setSettings(saved);
      setPersisted(saved);
      setApiKey('');
      setClearApiKey(false);
      toast.success('Configurações salvas.');
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao salvar o motor de decisão.'));
    } finally {
      setSaving(false);
    }
  }

  async function handleLocal(action: 'start' | 'stop' | 'use' | 'reset'): Promise<void> {
    if (!canWrite || !settings || localBusy) {
      return;
    }

    setLocalBusy(true);
    if (action === 'start') {
      closeConsoleWhenReady.current = true;
      setConsoleOpen(true);
    } else if (action !== 'use') {
      closeConsoleWhenReady.current = false;
      setConsoleOpen(true);
    }
    try {
      if (action === 'use') {
        const address = localStatus?.address || 'http://127.0.0.1:8000';
        const saved = await saveDecisionEngineSettings({
          baseUrl: address,
          clearApiKey: false,
          defaultLanguage: settings.defaultLanguage,
          checkpoint: 'multilingual',
          timeoutSeconds: settings.timeoutSeconds,
          maxConcurrent: settings.maxConcurrent,
          maxConcurrentPerCaller: settings.maxConcurrentPerCaller,
          circuitFailureThreshold: settings.circuitFailureThreshold,
          circuitBreakSeconds: settings.circuitBreakSeconds,
          enabled: settings.enabled,
          customerSuccessEnabled: settings.customerSuccessEnabled,
          emailTriageEnabled: settings.emailTriageEnabled,
          chatMessagesEnabled: settings.chatMessagesEnabled,
          technologyEnabled: settings.technologyEnabled,
          monitoringEnabled: settings.monitoringEnabled,
        });
        setSettings(saved);
        setPersisted(saved);
        toast.success('Endereço local gravado. Deixe a chave em branco.');
        return;
      }

      if (action === 'reset') {
        const confirmed = window.confirm(
          'Apagar o ambiente local e o modelo baixado? Na próxima inicialização o sistema instala e baixa de novo.',
        );
        if (!confirmed) {
          return;
        }
      }

      const status = action === 'start'
        ? await startDecisionEngineLocal()
        : action === 'reset'
          ? await resetDecisionEngineLocal()
          : await stopDecisionEngineLocal();
      setLocalStatus(status);
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao controlar o motor local.'));
    } finally {
      setLocalBusy(false);
    }
  }

  async function handleTest(): Promise<void> {
    if (!canWrite || testing) {
      return;
    }

    setTesting(true);
    setTestResult(null);
    try {
      const parsed = parseOptionLines(probeOptions, probeType);
      setTestedLevels(parsed.levels ?? []);
      const result = await testDecisionEngine({
        state: probeState,
        type: probeType,
        instructions: probeInstructions,
        criteria: parsed.criteria,
        levels: parsed.levels,
      });
      setTestResult(result);
      if (result.succeeded) {
        toast.success('Conexão respondida.');
      } else {
        toast.error(result.message || 'O motor não respondeu.');
      }
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao testar a conexão.'));
    } finally {
      setTesting(false);
    }
  }

  if (loading) {
    return <LoadingSpinner label="Carregando motor de decisão..." />;
  }

  if (!settings || !persisted) {
    return (
      <PageShell>
        <PageHeader
          title="Motor de decisão"
          description="Endereço, limites e teste de conexão usados por todos os módulos desta empresa."
          help={<DecisionEngineHelp />}
        />
        <PageCard title="Configurações">
          <p className="text-sm text-red-800">{loadError || 'Não foi possível carregar as configurações.'}</p>
        </PageCard>
      </PageShell>
    );
  }

  const addressReady = Boolean(persisted.baseUrl.trim());

  return (
    <PageShell>
      <PageHeader
        title="Motor de decisão"
        description="Endereço, limites e teste de conexão usados por todos os módulos desta empresa."
        help={<DecisionEngineHelp />}
      />

      <SettingsTabBar tabs={TABS} activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === 'settings' ? (
        <form onSubmit={(event) => void handleSave(event)} className="erp-form">
          <PageCard
            role="tabpanel"
            aria-label="Configurações"
            title="Configurações"
            subtitle="Valores gravados para esta empresa. Sem endereço salvo, o motor não é chamado."
          >
            <div className="erp-form-grid">
              <label className="erp-label erp-form-grid-full">
                Endereço
                <input
                  className="erp-input"
                  value={settings.baseUrl}
                  placeholder="http://motor:8000"
                  disabled={!canWrite}
                  onChange={(event) => setSettings({ ...settings, baseUrl: event.target.value })}
                />
              </label>
              <label className="erp-label">
                Chave de API
                <PasswordInput
                  className="erp-input pr-11 !mt-0"
                  wrapperClassName="mt-1 w-full"
                  value={apiKey}
                  placeholder={settings.apiKeyConfigured ? 'Chave já salva' : 'Opcional'}
                  disabled={!canWrite || clearApiKey}
                  onChange={(event) => setApiKey(event.target.value)}
                />
              </label>
              <label className="erp-label-inline sm:pt-8">
                <input
                  type="checkbox"
                  checked={clearApiKey}
                  disabled={!canWrite || !settings.apiKeyConfigured}
                  onChange={(event) => setClearApiKey(event.target.checked)}
                />
                Remover chave salva
              </label>
              <label className="erp-label">
                Idioma padrão
                <select
                  className="erp-input"
                  value={settings.defaultLanguage}
                  disabled={!canWrite}
                  onChange={(event) =>
                    setSettings({ ...settings, defaultLanguage: event.target.value })
                  }
                >
                  {LANGUAGES.some((item) => item.value === settings.defaultLanguage) ? null : (
                    <option value={settings.defaultLanguage}>
                      {settings.defaultLanguage} ({settings.defaultLanguage})
                    </option>
                  )}
                  {LANGUAGES.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label} ({item.value})
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Modelo
                <select
                  className="erp-input"
                  value={settings.checkpoint}
                  disabled={!canWrite}
                  onChange={(event) => setSettings({ ...settings, checkpoint: event.target.value })}
                >
                  {CHECKPOINTS.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Tempo limite (segundos)
                <input
                  type="number"
                  min={1}
                  max={5}
                  className="erp-input"
                  value={settings.timeoutSeconds}
                  disabled={!canWrite}
                  onChange={(event) =>
                    setSettings({ ...settings, timeoutSeconds: Number(event.target.value) })
                  }
                />
              </label>
              <label className="erp-label">
                Chamadas simultâneas da empresa
                <input
                  type="number"
                  min={1}
                  max={8}
                  className="erp-input"
                  value={settings.maxConcurrent}
                  disabled={!canWrite}
                  onChange={(event) =>
                    setSettings({ ...settings, maxConcurrent: Number(event.target.value) })
                  }
                />
              </label>
              <label className="erp-label">
                Chamadas simultâneas por origem
                <input
                  type="number"
                  min={1}
                  max={2}
                  className="erp-input"
                  value={settings.maxConcurrentPerCaller}
                  disabled={!canWrite}
                  onChange={(event) =>
                    setSettings({
                      ...settings,
                      maxConcurrentPerCaller: Number(event.target.value),
                    })
                  }
                />
              </label>
              <label className="erp-label">
                Falhas até isolar
                <input
                  type="number"
                  min={3}
                  max={20}
                  className="erp-input"
                  value={settings.circuitFailureThreshold}
                  disabled={!canWrite}
                  onChange={(event) =>
                    setSettings({
                      ...settings,
                      circuitFailureThreshold: Number(event.target.value),
                    })
                  }
                />
              </label>
              <label className="erp-label">
                Isolamento (segundos)
                <input
                  type="number"
                  min={5}
                  max={60}
                  className="erp-input"
                  value={settings.circuitBreakSeconds}
                  disabled={!canWrite}
                  onChange={(event) =>
                    setSettings({ ...settings, circuitBreakSeconds: Number(event.target.value) })
                  }
                />
              </label>
            </div>
            <p className="erp-hint">
              Vale o menor limite de chamadas simultâneas. Uma empresa ocupada não segura as outras:
              a chamada que não encontra vaga falha na hora.
            </p>
            <div className="mt-4">
              <button type="submit" className="erp-btn-primary" disabled={!canWrite || saving}>
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </PageCard>
        </form>
      ) : null}

      {activeTab === 'usage' ? (
        <form onSubmit={(event) => void handleSave(event)}>
          <PageCard
            role="tabpanel"
            aria-label="Uso"
            title="Uso"
            subtitle="Desligar um módulo interrompe a consulta ao motor só nesse módulo."
          >
            <label className="erp-label-inline">
              <input
                type="checkbox"
                checked={settings.enabled}
                disabled={!canWrite}
                onChange={(event) => setSettings({ ...settings, enabled: event.target.checked })}
              />
              Motor ativo no sistema
            </label>
            <div className={`mt-4 space-y-4 ${settings.enabled ? '' : 'opacity-60'}`}>
              <fieldset className="rounded-xl border border-secondary-200 p-3" disabled={!canWrite || !settings.enabled}>
                <label className="erp-label-inline font-medium">
                  <input
                    type="checkbox"
                    checked={settings.customerSuccessEnabled}
                    onChange={(event) =>
                      setSettings({ ...settings, customerSuccessEnabled: event.target.checked })
                    }
                  />
                  Sucesso do cliente
                </label>
                <div className={`mt-2 space-y-2 pl-6 ${settings.customerSuccessEnabled ? '' : 'opacity-60'}`}>
                  <label className="erp-label-inline">
                    <input
                      type="checkbox"
                      checked={settings.emailTriageEnabled}
                      disabled={!settings.customerSuccessEnabled}
                      onChange={(event) =>
                        setSettings({ ...settings, emailTriageEnabled: event.target.checked })
                      }
                    />
                    Triagem de e-mails
                  </label>
                  <p className="erp-hint">
                    As regras do canal continuam valendo. O motor faz a verificação final e pode ignorar spam, propaganda ou aviso de provedor que passou por elas. Também escolhe o serviço do catálogo.
                  </p>
                  <label className="erp-label-inline">
                    <input
                      type="checkbox"
                      checked={settings.chatMessagesEnabled}
                      disabled={!settings.customerSuccessEnabled}
                      onChange={(event) =>
                        setSettings({ ...settings, chatMessagesEnabled: event.target.checked })
                      }
                    />
                    Mensagens do chat
                  </label>
                </div>
              </fieldset>
              <fieldset className="rounded-xl border border-secondary-200 p-3" disabled={!canWrite || !settings.enabled}>
                <label className="erp-label-inline font-medium">
                  <input
                    type="checkbox"
                    checked={settings.technologyEnabled}
                    onChange={(event) =>
                      setSettings({ ...settings, technologyEnabled: event.target.checked })
                    }
                  />
                  Tecnologia
                </label>
                <div className={`mt-2 space-y-2 pl-6 ${settings.technologyEnabled ? '' : 'opacity-60'}`}>
                  <label className="erp-label-inline">
                    <input
                      type="checkbox"
                      checked={settings.monitoringEnabled}
                      disabled={!settings.technologyEnabled}
                      onChange={(event) =>
                        setSettings({ ...settings, monitoringEnabled: event.target.checked })
                      }
                    />
                    Monitoramento
                  </label>
                  <p className="erp-hint">
                    Quando o monitoramento consultar o motor, esta opção decide se a consulta acontece.
                  </p>
                </div>
              </fieldset>
            </div>
            <div className="mt-4">
              <button type="submit" className="erp-btn-primary" disabled={!canWrite || saving}>
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </PageCard>
        </form>
      ) : null}

      {activeTab === 'connection' ? (
        <PageCard
          role="tabpanel"
          aria-label="Conexão"
          title="Conexão"
          subtitle="O teste usa o endereço e a chave já gravados. Salve antes de testar."
        >
          {localStatus?.available ? (
            <div className="erp-alert-warning mb-4">
              <p>Desenvolvimento nesta máquina</p>
              <p className="mt-1 font-normal">
                Sobe o motor no Windows, sem Docker, e baixa o modelo na primeira vez.
                {localStatus.address ? ` Endereço: ${localStatus.address}.` : ''} Sem chave.
              </p>
              <p className="mt-1 font-normal">{localStatus.message}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  className="erp-btn-primary erp-btn-sm"
                  disabled={!canWrite || localBusy || localStatus.running}
                  onClick={() => void handleLocal('start')}
                >
                  {localStatus.starting || localBusy ? 'Iniciando...' : 'Iniciar motor local'}
                </button>
                <button
                  type="button"
                  className="erp-btn-secondary erp-btn-sm"
                  disabled={!canWrite || localBusy || (!localStatus.running && !localStatus.starting)}
                  onClick={() => void handleLocal('stop')}
                >
                  Parar
                </button>
                <button
                  type="button"
                  className="erp-btn-integration erp-btn-sm"
                  disabled={!canWrite || localBusy || saving}
                  onClick={() => void handleLocal('use')}
                >
                  Usar este endereço
                </button>
                <button
                  type="button"
                  className="erp-btn-danger erp-btn-sm"
                  disabled={!canWrite || localBusy || localStatus.starting}
                  onClick={() => void handleLocal('reset')}
                >
                  Recriar ambiente
                </button>
                <button
                  type="button"
                  className="erp-btn-secondary erp-btn-sm"
                  onClick={() => setConsoleOpen(true)}
                >
                  Console
                </button>
              </div>
            </div>
          ) : null}
          <p className="mb-3 text-sm text-secondary-600">
            {persisted.baseUrl.trim()
              ? 'Endereço salvo para esta empresa.'
              : 'Nenhum endereço salvo. Grave um endereço antes de testar.'}
            {persisted.apiKeyConfigured ? ' Chave salva.' : ' Sem chave salva.'}
          </p>
          <div className="erp-form-grid">
            <label className="erp-label erp-form-grid-full">
              Texto de exemplo
              <textarea
                className="erp-input min-h-28"
                value={probeState}
                disabled={!canWrite || testing}
                onChange={(event) => setProbeState(event.target.value)}
              />
            </label>
            <label className="erp-label">
              Tipo
              <select
                className="erp-input"
                value={probeType}
                disabled={!canWrite || testing}
                onChange={(event) => {
                  const next = event.target.value as QuestionType;
                  setProbeType(next);
                  setProbeInstructions(instructionsFor(next));
                  setProbeOptions(optionsFor(next));
                }}
              >
                <option value="choice">Escolha</option>
                <option value="score">Nota</option>
                <option value="noul">Sim ou não</option>
              </select>
            </label>
            <label className="erp-label">
              Instruções
              <input
                className="erp-input"
                value={probeInstructions}
                disabled={!canWrite || testing}
                onChange={(event) => setProbeInstructions(event.target.value)}
              />
            </label>
            {probeType !== 'noul' ? (
              <label className="erp-label erp-form-grid-full">
                {probeType === 'score'
                  ? 'Níveis, um por linha'
                  : 'Opções, uma por linha (chave: descrição)'}
                <textarea
                  className="erp-input min-h-28"
                  value={probeOptions}
                  disabled={!canWrite || testing}
                  onChange={(event) => setProbeOptions(event.target.value)}
                />
              </label>
            ) : null}
          </div>
          <div className="mt-4">
            <button
              type="button"
              className="erp-btn-secondary"
              disabled={!canWrite || testing || saving || !addressReady}
              onClick={() => void handleTest()}
            >
              {testing ? 'Testando...' : 'Testar conexão'}
            </button>
          </div>
          {testResult ? (
            <div className="mt-4 rounded-xl border border-secondary-200 bg-secondary-50 p-3 text-sm">
              <p className={testResult.succeeded ? 'text-emerald-800' : 'text-red-800'}>
                {testResult.message} ({testResult.elapsedMilliseconds} ms)
              </p>
              {testResult.answer ? (
                <div className="mt-2 space-y-1 text-secondary-700">
                  <p>Resposta: {formatAnswer(testResult.answer, testedLevels)}</p>
                  {testResult.answer.answerConfidence != null ? (
                    <p>Confiança: {(testResult.answer.answerConfidence * 100).toFixed(1)}%</p>
                  ) : null}
                  {Object.keys(testResult.answer.probabilities).length > 0 ? (
                    <ul className="list-disc pl-5">
                      {Object.entries(testResult.answer.probabilities).map(([key, value]) => (
                        <li key={key}>
                          {testResult.answer?.type === 'score'
                            ? scoreLevelName(key, testedLevels, testResult.answer.legend)
                            : key}
                          : {(value * 100).toFixed(1)}%
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
        </PageCard>
      ) : null}

      <DraggableModalPanel
        open={consoleOpen}
        title="Console do motor local"
        subtitle={localStatus?.message}
        onClose={() => setConsoleOpen(false)}
        className="w-full max-w-3xl"
        bodyClassName="bg-secondary-900 p-0"
      >
        <pre
          ref={consoleRef}
          className="max-h-[28rem] min-h-64 overflow-auto whitespace-pre-wrap break-words bg-secondary-900 p-4 font-mono text-xs leading-5 text-emerald-100"
        >
          {(localStatus?.lines ?? []).join('\n') || 'Sem saída ainda.'}
        </pre>
      </DraggableModalPanel>
    </PageShell>
  );
}
